import { State, Action, StateContext, Selector, NgxsOnInit, Store } from '@ngxs/store';
import { AuthStateModel } from './auth.state.model';
import { 
  LoginWithBiodigital,
  LoginSuccess,
  LoginFailure,
  GetUserProfile,
  Logout,
  LogoutSuccess,
  IsLoggedIn,
  IsLoggedInSuccess,
  IsLoggedInFailure,
  GetUserProfileSuccess,
  GetUserProfileFailure, 
  CheckSession,
  CheckProfile,
  CheckProfileSuccess,
  CheckProfileFailure,
  GetEnvironmentSettings,
  GetEnvironmentSettingsSuccess,
  GetEnvironmentSettingsFailure,
  ResetPassword,
  ResetPasswordSuccess,
  ResetPasswordFailure,
  SignUp,
  SignUpSuccess,
  SignUpFailure,
  LoginWithGoogle,
  LoginWithFacebook,
  SetUserLocale,
  SetUserLocaleSuccess,
  CheckLocale,
  GetUserApps,
  SetSelectedUserApp,
  SetUserApps,
  CheckSelectedUserApp,
  SetUserMessageViewed,
  SetUserMessageViewedSuccess,
  SetUserMessageViewedFail,
  ResetAuth,
  GetUserMessage,
  GetUserMessageFail,
  GetUserMessageSuccess,
  LogoutFailure
} from './auth.actions';
import { AuthService } from '../../auth.service';
import { SetOrganizations, ResetOrganizations } from 'src/app/organizations/state/organizations.actions';
import { finalize } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { LOCALE_ID, Inject } from '@angular/core';
import { StorageService, StorageItemEnum } from 'src/app/shared/storage.service';
import { locales, defaultLocale } from 'src/app/locale-config.module';
import { ResetBookmarks } from 'src/app/bookmarks/state/bookmarks.actions';
import { ResetEditPage } from 'src/app/edit/state/edit.actions';
import { ResetEnginePosition } from 'src/app/engine/state/engine.actions';
import { ResetExplore } from 'src/app/explore/state/explore.actions';
import { ResetHistory } from 'src/app/history/state/history.actions';
import { ResetLabels } from 'src/app/story/state/labels/story.labels.actions';
import { ResetStory } from 'src/app/story/state/story.actions';
import { ResetStoryChapters } from 'src/app/story/state/chapters/story.chapters.actions';
import { ResetView } from 'src/app/classic-view/state/classic-view.actions';
import { ResetLayout } from 'src/app/layout/state/layout.actions';
import { ResetEngineNodes } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { UserMessageService } from 'src/app/shared/user-message.service';
import { ErrorService, ErrorMessage } from 'src/app/shared/error.service';
import { get } from 'lodash';
import { GalleryActions } from 'src/app/gallery/state/gallery.actions';


const initialState = {
  loading: false,
  loggedIn: null,
  userLocale: null
};

@State<AuthStateModel>({
  name: 'auth',
  defaults: initialState
})
export class AuthState implements NgxsOnInit {
  /**
   * Selectors
   */
  @Selector()
  static getLoggedIn(state: AuthStateModel) {
    return state.loggedIn;
  }

  @Selector()
  static getEnvironmentSettings(state: AuthStateModel) {
    return state.environmentSettings;
  }

  @Selector()
  static getUserProfile(state: AuthStateModel) {
    return state.userProfile;
  }

  @Selector()
  static getProfileCheck(state: AuthStateModel) {
    return state.profileCheck;
  }

  @Selector()
  static getLoading(state: AuthStateModel) {
    return state.loading;
  }

  @Selector()
  static getUserLocale(state: AuthStateModel) {
    return state.userLocale;
  }

  @Selector()
  static getUserLanguage(state: AuthStateModel): string {
    return get(state, 'userLocale.lang', defaultLocale.lang);
  }

  @Selector()
  static getUserApps(state: AuthStateModel) {
    return get(state, 'userApps', []);
  }

  @Selector()
  static getSelectedUserApp(state: AuthStateModel) {
    const apps = state.userApps || [];
    return apps.find((app) => app.id === state.selectedAppId);
  }

  @Selector()
  static isPremiumUser(state: AuthStateModel) {
    return get(state, 'userProfile.premium_account_valid') === true;
  }

  /**
   * Actions
   */
  @Action(IsLoggedIn)
  isLoggedIn(context: StateContext<AuthStateModel>, action: IsLoggedIn) {
    context.patchState({ loading: true });

    this.authService.isLoggedIn()
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        (result: { signed_in: boolean }) => {
          if (result.signed_in) {
            context.dispatch(new IsLoggedInSuccess());
          } else {
            context.dispatch(new IsLoggedInFailure());
          }
        });
  }
  
  @Action(IsLoggedInSuccess)
  isLoggedInSuccess(context: StateContext<AuthStateModel>, action: IsLoggedInSuccess) {
    context.patchState({
      loggedIn: true
    });
  }

  @Action(IsLoggedInFailure)
  isLoggedInFailure(context: StateContext<AuthStateModel>, action: IsLoggedInFailure) {
    context.patchState({
      loggedIn: false
    });
  }


  @Action(LoginWithBiodigital)
  login(context: StateContext<AuthStateModel>, action: LoginWithBiodigital) {
    context.patchState({ loading: true });

    this.authService.loginWithBiodigital(action.login)
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        () => {
          context.dispatch(new LoginSuccess());
        },
        (error) => {
          this.logFailure('LOGIN_FAILURE', error);
          context.dispatch(new LoginFailure(error, action.login));
        });
  }

  @Action(LoginWithGoogle)
  loginWithGoogle(context: StateContext<AuthStateModel>, action: LoginWithGoogle) {
    context.patchState({ loading: true });

    this.authService.loginWithGoogle(action.login)
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        () => {
          context.dispatch(new LoginSuccess());
        },
        (error: HttpErrorResponse) => {
          if (action.login.groupCodeMode === 1 || action.login.groupInviteMode === 1) {
            this.logFailure('GOOGLE_SIGNUP_FAILURE', error);
          } 
          
          context.dispatch(new LoginFailure(error, action.login));
        });
  }

  // Deprecated
  @Action(LoginWithFacebook)
  loginWithFacebook(context: StateContext<AuthStateModel>, action: LoginWithFacebook) {
    context.patchState({ loading: true });

    this.authService.loginWithFacebook(action.login)
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        () => {
          context.dispatch(new LoginSuccess());
        },
        (error: HttpErrorResponse) => {
          context.dispatch(new LoginFailure(error, action.login));
        });
  }

  @Action(LoginSuccess)
  loginSuccess(context: StateContext<AuthStateModel>, action: LoginSuccess) {
    context.dispatch(new IsLoggedIn());
    context.dispatch(new CheckLocale());
  }

  @Action(Logout)
  logout(context: StateContext<AuthStateModel>, action: Logout) {
    context.patchState({ loading: true });

    this.authService.logout()
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        () => context.dispatch(new LogoutSuccess()),
        () => context.dispatch(new LogoutFailure())
      );
  }

  @Action(LogoutSuccess)
  logoutSuccess(context: StateContext<AuthStateModel>, action: LogoutSuccess) {
    context.setState(initialState);

    context.dispatch(new ResetAuth());
    context.dispatch(new ResetBookmarks());
    context.dispatch(new ResetEditPage());
    context.dispatch(new ResetEnginePosition());
    context.dispatch(new ResetEngineNodes());
    context.dispatch(new ResetExplore());
    context.dispatch(new ResetHistory());
    context.dispatch(new ResetLabels());
    context.dispatch(new ResetLayout());
    context.dispatch(new ResetOrganizations());
    context.dispatch(new ResetStory());
    context.dispatch(new ResetStoryChapters());
    context.dispatch(new ResetView());
    context.dispatch(new GalleryActions.Reset());    

    // clear cached storage
    this.storageService.clearSessionStorage();
  }

  @Action(CheckProfile)
  checkProfile(context: StateContext<AuthStateModel>, action: CheckProfile) {
    context.patchState({ loading: true });

    return this.authService.checkProfile(action.email)
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        (profile) => {
          context.dispatch(new CheckProfileSuccess(profile));
        },
        (error) => {
          context.dispatch(new CheckProfileFailure(error));
        }
      );
  }

  @Action(CheckProfileSuccess)
  checkProfileSuccess(context: StateContext<AuthStateModel>, action: CheckProfileSuccess) {
    context.patchState({
      profileCheck: action.profile
    });
  }

  @Action(GetUserProfile)
  getUserProfile(context: StateContext<AuthStateModel>, action: GetUserProfile) {
    context.patchState({ loading: true });

    return this.authService.getUserProfile()
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        (userProfile) => {
          context.dispatch(new GetUserProfileSuccess(userProfile));
        },
        (error) => {
          context.dispatch(new GetUserProfileFailure(error));
        }
      );
  }

  @Action(GetUserProfileSuccess)
  getUserProfileSuccess(context: StateContext<AuthStateModel>, action: GetUserProfileSuccess) {
    context.patchState({
      userProfile: action.userProfile
    });

    context.dispatch(new SetOrganizations(action.userProfile.organizations));
    context.dispatch(new GetUserApps());
    context.dispatch(new CheckLocale());
  }

  @Action(GetUserApps)
  getUserApps(context: StateContext<AuthStateModel>, action: GetUserApps) {
    this.authService.getUserApps()
      .subscribe(
        (apps) => {
          context.dispatch(new SetUserApps(apps));
          context.dispatch(new CheckSelectedUserApp());
        },
        // #TODO: handle errors
        (err) => {

        });
  }

  @Action(SetUserApps)
  setUserApps(context: StateContext<AuthStateModel>, { apps }: SetUserApps) {
    context.patchState({
      userApps: apps
    });
  }

  @Action(SetSelectedUserApp)
  setSelectedUserApp(context: StateContext<AuthStateModel>, { appId }: SetSelectedUserApp) {
    context.patchState({
      selectedAppId: appId
    });
    // update storage
    if (appId !== null) {
      this.storageService.setLocalItem(StorageItemEnum.UserAppId, appId);
    } else {
      this.storageService.removeLocalItem(StorageItemEnum.UserAppId);
    }
  }

  @Action(CheckSelectedUserApp)
  checkSelectedUserApp(context: StateContext<AuthStateModel>, action: CheckSelectedUserApp) {
    const apps = context.getState().userApps;
    const storedAppId = this.storageService.getLocalItem(StorageItemEnum.UserAppId);
    if (storedAppId) { // set stored app
      const appId = parseInt(storedAppId, 10);
      context.dispatch(new SetSelectedUserApp(appId));
    } else if (apps && apps.length) { // set default app
      context.dispatch(new SetSelectedUserApp(apps[0].id));
    }
  }

  @Action(CheckSession)
  checkSession(context: StateContext<AuthStateModel>, action: CheckSession) {
    context.patchState({ loading: true });

    this.authService.isLoggedIn()
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe((result: { signed_in: boolean }) => {
        if (result.signed_in) {
          context.dispatch(new IsLoggedInSuccess());
          context.dispatch(new GetUserProfile());
        } else {
          context.dispatch(new IsLoggedInFailure());
        }
      });
  }

  @Action(GetEnvironmentSettings)
  getEnvironmentSettings(context: StateContext<AuthStateModel>, action: GetEnvironmentSettings) {
    context.patchState({ loading: true });

    return this.authService.getEnvironmentSettings()
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        (environmentSettings) => {
          context.dispatch(new GetEnvironmentSettingsSuccess(environmentSettings));
        },
        (error) => {
          context.dispatch(new GetEnvironmentSettingsFailure(error));
        }
      );
  }

  @Action(GetEnvironmentSettingsSuccess)
  GetEnvironmentSettingsSuccess(context: StateContext<AuthStateModel>, action: GetEnvironmentSettingsSuccess) {
    context.patchState({
      environmentSettings: action.environmentSettings
    });
  }

  @Action(ResetPassword)
  resetPassword(context: StateContext<AuthStateModel>, action: ResetPassword) {
    context.patchState({ loading: true });

    this.authService.resetPassword(action.email)
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        () => {
          context.dispatch(new ResetPasswordSuccess(action.email));
        },
        (error) => {
          context.dispatch(new ResetPasswordFailure(error));
        }
      );
  }

  @Action(SignUp)
  signUp(context: StateContext<AuthStateModel>, action: SignUp) {
    context.patchState({ loading: true });

    return this.authService.signUp(action.value)
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe(
        () => {
          context.dispatch(new SignUpSuccess());
        },
        (error) => {
          context.dispatch(new SignUpFailure(error));
        }
      );
  }

  @Action(SignUpFailure)
  signUpFailure(context: StateContext<AuthStateModel>, action: SignUpFailure) {
    this.logFailure('BIODIGITAL_SIGNUP_FAILURE', action);
  }

  @Action(SetUserLocale)
  setUserLocale(context: StateContext<AuthStateModel>, { locale }: SetUserLocale) {
    // set storage
    this.storageService.setLocalItem(StorageItemEnum.Locale, locale.id);

    context.patchState({ userLocale: locale });
    context.dispatch(new SetUserLocaleSuccess(locale));
  }

  @Action(CheckLocale)
  checkLocale(context: StateContext<AuthStateModel>, action: CheckLocale) {
    const targetLocale = locales.find((locale) => locale.id === this.localeId) || defaultLocale;
    context.dispatch(new SetUserLocale(targetLocale));
  }

  @Action(GetUserMessage)
  getUserMessage(context: StateContext<AuthStateModel>, { type, mode }: GetUserMessage) {
    this.userMessageService.getUserMessage(type, mode)
      .subscribe(
        (message) => {
          context.dispatch(new GetUserMessageSuccess(message));
        },
        () => {
          context.dispatch(new GetUserMessageFail(type));
        });
  } 

  @Action(SetUserMessageViewed)
  setUserMessageViewed(context: StateContext<AuthStateModel>, { messageSeenId }: SetUserMessageViewed) {
    this.userMessageService.setUserMessageViewed(messageSeenId)
      .subscribe(
        () => context.dispatch(new SetUserMessageViewedSuccess()),
        () => context.dispatch(new SetUserMessageViewedFail())
      );
  }  

  @Action(ResetAuth)
  resetAuth(context: StateContext<AuthStateModel>, action: ResetAuth) {
    context.setState(initialState);
  }

  private logFailure(category: string, error: any) {
    const message = get(error, 'message') || '';
    const log = get(error, 'error.error.message') || '';
    this.errorService.logError(new ErrorMessage(category, message, log)).subscribe();
  }

  constructor(
    private authService: AuthService,
    private userMessageService: UserMessageService,
    private storageService: StorageService,
    @Inject(LOCALE_ID) private localeId: string,
    private store: Store,
    private errorService: ErrorService
  ) { }

  ngxsOnInit(context: StateContext<AuthStateModel>) {
    context.dispatch(new CheckSession());
    context.dispatch(new CheckLocale());
    context.dispatch(new CheckSelectedUserApp());
  }
}
